import memoize from 'fast-memoize';
import sortBy from 'lodash.sortby';
import { DELTA_PERCENTAGE } from '../../pages/dashboard/balance-sheet/types';
import { DATA_NOT_AVAILABLE_VALUE } from '../api/api-interfaces';
import { CompanySettingsStore } from '../company-settings-store';
import { DataTypes } from '../constants/constants';
import {
  MetricCategoryId,
  MetricGroupId,
  MetricId as GraphQLMetricId,
  MetricType,
} from '../graphql/generated/graphql-sdk';
import { localStore } from '../local-store';
import { IPermissionsStore } from '../permissions/permissions-store';
import { isValidNumber } from '../utilFunctions/validators';
import {
  MetricDetails,
  MetricGroupDetails,
  MetricLifecycleStageEnum,
  MetricTree,
} from '../v2/api/metrics/types';
import { MetricFormatTypeId } from '../v2/api/types-graphql';
import { metricIdNeedsEmptyCohortFilter } from '../v2/api/utils';
import { TimeDisplayFormat } from '../v2/types';
import {
  convertDecimalHoursToHHMM,
  formatPercentageNumber,
  metricFormatTypeToFormatterMap,
} from '../v2/utils-display';

import { action, computed, observable } from 'mobx';
import { Hasher } from '../v2/api/hasher';
import {
  metricTreeToMetricDetailsMap,
  metricTreeToMetricGroupDetailsMap,
} from '../v2/api/metrics/utils';
import { HasDependencies, hashString } from '../v2/api/types';
import { LanguageString } from '../v2/common/components/dashboards/types';
import { MetricId } from '../v2/common/components/metrics/types';
import { Languages } from '../v2/constants';

export class MetricStore implements HasDependencies {
  private readonly TIME_DISPLAY_FORMAT_STORAGE_KEY_PREFIX =
    'TIME_DISPLAY_FORMAT_BY_METRIC_ID';
  private readonly GLOBAL_TIME_DISPLAY_FORMAT_STORAGE_KEY_PREFIX =
    'TIME_DISPLAY_FORMAT_GLOBAL';

  public constructor(
    readonly permissionsStore: IPermissionsStore,
    readonly companySettingsStore: CompanySettingsStore,
    readonly selectedLanguage: Languages,
    readonly metricTree: MetricTree
  ) {
    this.timeDisplayFormatByMetricId.replace(
      this.loadTimeDisplayFormatFromStorage()
    );
    this.globalTimeDisplayFormat =
      this.loadGlobalTimeDisplayFormatFromStorage();
    // Drop leftover overrides from the previous persist format, which wrote
    // every per-row click even when it matched the global default.
    if (this.pruneTimeDisplayOverridesMatchingGlobal()) {
      this.persistTimeDisplayFormatToStorage();
    }
  }

  public getDeps = () => [
    hashString([
      ...this.permissionsStore.getDeps(),
      ...this.companySettingsStore.getDeps(),
      this.selectedLanguage,
      Hasher.hash(this.metricTree),
    ]),
  ];

  // How "hours" format-type metric values are displayed on the People Balance
  // Sheet: `globalTimeDisplayFormat` is the default for every hours metric (set
  // from the sidebar Display Settings), and `timeDisplayFormatByMetricId` holds
  // per-metric overrides that *differ* from that default (set from the per-row
  // clock). Effective format is always override ?? global, and is used everywhere
  // a metric renders (PBS table, charts, factbox, drill-down). Both are persisted
  // per domain in local storage. Use ObservableMap so .set/.delete/.clear notify
  // observers — assigning a plain Map in the constructor would drop reactivity.
  @observable
  public globalTimeDisplayFormat: TimeDisplayFormat = TimeDisplayFormat.DECIMAL;

  @observable
  public timeDisplayFormatByMetricId = observable.map<
    GraphQLMetricId,
    TimeDisplayFormat
  >();

  private get timeDisplayFormatStorageKey(): string {
    return `${this.TIME_DISPLAY_FORMAT_STORAGE_KEY_PREFIX}-${this.companySettingsStore.domain}`;
  }

  private get globalTimeDisplayFormatStorageKey(): string {
    return `${this.GLOBAL_TIME_DISPLAY_FORMAT_STORAGE_KEY_PREFIX}-${this.companySettingsStore.domain}`;
  }

  private loadGlobalTimeDisplayFormatFromStorage = (): TimeDisplayFormat => {
    return localStore.get(this.globalTimeDisplayFormatStorageKey) ===
      TimeDisplayFormat.HH_MM
      ? TimeDisplayFormat.HH_MM
      : TimeDisplayFormat.DECIMAL;
  };

  private isStoredTimeDisplayFormat = (
    value: unknown
  ): value is TimeDisplayFormat => {
    return (
      value === TimeDisplayFormat.HH_MM || value === TimeDisplayFormat.DECIMAL
    );
  };

  private loadTimeDisplayFormatFromStorage = (): [
    GraphQLMetricId,
    TimeDisplayFormat,
  ][] => {
    try {
      const raw = localStore.get(this.timeDisplayFormatStorageKey);
      if (!raw) {
        return [];
      }
      return (
        Object.entries(JSON.parse(raw) as Record<string, unknown>) as [
          GraphQLMetricId,
          unknown,
        ][]
      ).filter((entry): entry is [GraphQLMetricId, TimeDisplayFormat] =>
        this.isStoredTimeDisplayFormat(entry[1])
      );
    } catch (error) {
      // Corrupt or externally-edited storage value — fall back to no overrides,
      // matching how useStateWithLocalStorage handles an unparseable value.
      return [];
    }
  };

  private persistTimeDisplayFormatToStorage = () => {
    const asObject = Object.fromEntries(this.timeDisplayFormatByMetricId);
    localStore.set(this.timeDisplayFormatStorageKey, JSON.stringify(asObject));
  };

  private persistGlobalTimeDisplayFormatToStorage = () => {
    localStore.set(
      this.globalTimeDisplayFormatStorageKey,
      this.globalTimeDisplayFormat
    );
  };

  private pruneTimeDisplayOverridesMatchingGlobal = (): boolean => {
    let pruned = false;
    for (const [
      metricId,
      format,
    ] of this.timeDisplayFormatByMetricId.entries()) {
      if (format === this.globalTimeDisplayFormat) {
        this.timeDisplayFormatByMetricId.delete(metricId);
        pruned = true;
      }
    }
    return pruned;
  };

  public getTimeDisplayFormat = (
    metricId: GraphQLMetricId
  ): TimeDisplayFormat => {
    return (
      this.timeDisplayFormatByMetricId.get(metricId) ??
      this.globalTimeDisplayFormat
    );
  };

  // True when at least one hours metric is not following the global default.
  @computed
  public get hasTimeDisplayFormatOverrides(): boolean {
    return this.timeDisplayFormatByMetricId.size > 0;
  }

  // Hour/overtime metric groups. The PM Master currently tags all of these as
  // PMFT012_FloatingNumber1 or PMFT005_Count rather than PMFT010_Hours, and
  // the master can't be changed, so we allowlist them here instead.
  private static readonly HOURS_METRIC_GROUP_ALLOWLIST: ReadonlySet<MetricGroupId> =
    new Set([
      MetricGroupId.Pmg0213AvgWorkHoursPerPerson,
      MetricGroupId.Pmg0215MonthlyOt,
      MetricGroupId.Pmg0414NonStatutoryOvertime,
      MetricGroupId.Pmg0220HoursWorkedHoliday,
      MetricGroupId.Pmg0221HoursWorkedLegalHoliday,
      MetricGroupId.Pmg0222OtHours,
      MetricGroupId.Pmg0224TotalHolidayOt,
      MetricGroupId.Pmg0225TotalOt,
      MetricGroupId.Pmg0470OvertimeHolidayHours,
    ]);

  // A metric value can be shown as HH:MM when the metric tree tags it as an
  // hours value (PMFT010_Hours), or when it belongs to one of the
  // HOURS_METRIC_GROUP_ALLOWLIST groups above.
  public isTimeFormatToggleAvailable = (metricId: GraphQLMetricId): boolean => {
    const { formatType, metricGroupId } =
      this.getMetricDetailsFromMetricId(metricId);
    return (
      formatType === MetricFormatTypeId.Pmft010Hours ||
      MetricStore.HOURS_METRIC_GROUP_ALLOWLIST.has(metricGroupId)
    );
  };

  // True when the domain has at least one metric whose value can be shown as
  // HH:MM. Gates the sidebar "Show hours as HH:MM" toggle so it only appears
  // where it does something.
  @computed
  public get hasAnyTimeFormattableMetric(): boolean {
    return this.allMetricIds.some((id) => this.isTimeFormatToggleAvailable(id));
  }

  // Hours metrics the current domain/role can actually view. Used to decide
  // when every visible hours metric matches, so we can promote that format to
  // the global default and drop leftover overrides.
  private get allowedTimeFormattableMetricIds(): GraphQLMetricId[] {
    return this.allMetricGroupDetailsWithAllowedSortedMetricIds.flatMap(
      (group) =>
        group.dimensions
          .map((d) => d.id)
          .filter((id) => this.isTimeFormatToggleAvailable(id))
    );
  }

  private doAllAllowedTimeFormattableMetricsUse = (
    format: TimeDisplayFormat
  ): boolean => {
    const ids = this.allowedTimeFormattableMetricIds;
    return (
      ids.length > 0 &&
      ids.every((id) => this.getTimeDisplayFormat(id) === format)
    );
  };

  // Per-row toggle icon: flips the effective format for a single metric.
  // An override is stored only when it differs from the global default, so
  // flipping a row back onto the default (or flipping every hours metric to
  // the same format) leaves the sidebar switch and the clocks in agreement.
  @action
  public toggleTimeDisplayFormat = (metricId: GraphQLMetricId) => {
    const nextFormat =
      this.getTimeDisplayFormat(metricId) === TimeDisplayFormat.DECIMAL
        ? TimeDisplayFormat.HH_MM
        : TimeDisplayFormat.DECIMAL;
    if (nextFormat === this.globalTimeDisplayFormat) {
      this.timeDisplayFormatByMetricId.delete(metricId);
    } else {
      this.timeDisplayFormatByMetricId.set(metricId, nextFormat);
    }
    if (this.doAllAllowedTimeFormattableMetricsUse(nextFormat)) {
      this.globalTimeDisplayFormat = nextFormat;
      this.timeDisplayFormatByMetricId.clear();
      this.persistGlobalTimeDisplayFormatToStorage();
    }
    this.persistTimeDisplayFormatToStorage();
  };

  // Sidebar Display Settings: sets the default for every hours metric. Clears
  // per-metric overrides so the whole balance sheet follows the new setting.
  @action
  public setGlobalTimeDisplayFormat = (format: TimeDisplayFormat) => {
    this.globalTimeDisplayFormat = format;
    this.timeDisplayFormatByMetricId.clear();
    this.persistGlobalTimeDisplayFormatToStorage();
    this.persistTimeDisplayFormatToStorage();
  };

  @action
  public toggleGlobalTimeDisplayFormat = () => {
    this.setGlobalTimeDisplayFormat(
      this.globalTimeDisplayFormat === TimeDisplayFormat.DECIMAL
        ? TimeDisplayFormat.HH_MM
        : TimeDisplayFormat.DECIMAL
    );
  };

  @computed
  public get metricCategoryToSortPositionMap(): Record<
    MetricCategoryId,
    number
  > {
    return Object.fromEntries(
      this.metricTree.categoryDetails.map((category) => {
        return [category.id, category.defaultPosition];
      })
    ) as Record<MetricCategoryId, number>;
  }

  @computed
  public get metricGroupToSortPositionMap(): Record<MetricGroupId, number> {
    return Object.fromEntries(
      this.metricTree.groupDetails.map((group) => {
        return [group.id, group.defaultPosition];
      })
    ) as Record<MetricGroupId, number>;
  }

  @computed
  public get metricIdToSortPositionMap(): Record<MetricId, number> {
    return Object.fromEntries(
      this.metricTree.groupDetails.flatMap((group) => {
        return group.dimensions.map((d) => {
          return [d.id, d.defaultPosition];
        });
      })
    ) as Record<MetricId, number>;
  }

  @computed
  public get metricCategoryToNameMap(): Record<
    MetricCategoryId,
    LanguageString
  > {
    return Object.fromEntries(
      this.metricTree.categoryDetails.map((category) => {
        return [category.id, category.metricCategoryName];
      })
    ) as Record<MetricCategoryId, LanguageString>;
  }

  @computed
  public get metricGroupToNameMap(): Record<MetricGroupId, LanguageString> {
    return Object.fromEntries(
      this.metricTree.groupDetails.map((group) => {
        return [group.id, group.metricGroupName];
      })
    ) as Record<MetricGroupId, LanguageString>;
  }

  @computed
  public get metricIdToNameMap(): Record<MetricId, LanguageString> {
    return Object.fromEntries(
      this.metricTree.groupDetails.flatMap((group) => {
        return group.dimensions.map((d) => {
          return [d.id, d.metricName];
        });
      })
    ) as Record<MetricId, LanguageString>;
  }

  @computed
  public get allMetricGroupDetailsWithAllowedSortedMetricIds(): MetricGroupDetails[] {
    const metricDetailsWithAllowedMetricIds =
      this.metricTree.groupDetails.map((m) => {
        return {
          ...m,
          dimensions: sortBy(m.dimensions, (d) => d.defaultPosition).filter(
            (d) => {
              const isActiveOrDeprecated =
                d.lifecycleStage.__typename ===
                  MetricLifecycleStageEnum.MetricLifecycleStageActive ||
                d.lifecycleStage.__typename ===
                  MetricLifecycleStageEnum.MetricLifecycleStageDeprecated;
              const isDomainAllowed = this.domainAllowedMetricsSet.has(d.id);
              const isPermissionsAllowed = this.permissionsStore.canViewMetric(
                d.id as unknown as GraphQLMetricId
              );
              return (
                isActiveOrDeprecated && isDomainAllowed && isPermissionsAllowed
              );
            }
          ),
        };
      }) ?? [];
    return metricDetailsWithAllowedMetricIds;
  }

  @computed
  public get allMetricIds(): GraphQLMetricId[] {
    return this.metricTree.groupDetails.flatMap((group) =>
      group.dimensions.map((d) => d.id)
    );
  }

  @computed
  public get domainAllowedMetricsSet(): Set<GraphQLMetricId> {
    const domainAllowedMetricsSet = new Set(
      this.companySettingsStore.allowedMetricsForDomain
    );
    return domainAllowedMetricsSet;
  }

  @computed
  public get domainAllowedMetricGroupsSet(): Set<MetricGroupId> {
    const allMetricGroups = this.metricTree.groupDetails;
    // Domain Allowed metric groups would be those that have at least one domain allowed metric
    const domainAllowedMetricGroups = allMetricGroups
      .map((g) => {
        return {
          ...g,
          dimensions: g.dimensions.filter((d) =>
            this.domainAllowedMetricsSet.has(d.id)
          ),
        };
      })
      .filter((g) => g.dimensions.length)
      .map((g) => g.id);
    return new Set(domainAllowedMetricGroups);
  }

  @computed
  public get allMetricGroupIds(): MetricGroupId[] {
    return this.metricTree.groupDetails.map((g) => g.id);
  }

  public getTranslatedMetricCategoryName = (
    categoryId: MetricCategoryId
  ): string => {
    return (
      this.metricCategoryToNameMap?.[categoryId]?.[this.selectedLanguage] ??
      categoryId
    );
  };

  public getTranslatedMetricGroupName = (
    metricGroupId: MetricGroupId
  ): string => {
    return (
      this.metricGroupToNameMap?.[metricGroupId]?.[this.selectedLanguage] ??
      metricGroupId
    );
  };

  public getTranslatedMetricIdName = (metricId: GraphQLMetricId): string => {
    return (
      this.metricIdToNameMap?.[metricId]?.[this.selectedLanguage] ?? metricId
    );
  };

  public getPermissionsAllowedMetricGroups = (): MetricGroupId[] => {
    const allowedMetricGroups =
      this.allMetricGroupDetailsWithAllowedSortedMetricIds
        .filter((m) => m.dimensions.length)
        .map((m) => m.id);
    return allowedMetricGroups;
  };

  public getMetricGroupDetails = (
    metricGroupId: MetricGroupId
  ): MetricGroupDetails => {
    const metricGroupDetails = this.metricTree.groupDetails.find(
      (m) => m.id === metricGroupId
    );
    if (!metricGroupDetails) {
      throw new Error(`Metric not found - ${metricGroupId}`);
    } else {
      return metricGroupDetails;
    }
  };

  public getMetricGroupDetailsFromMetricId = memoize(
    (metric: GraphQLMetricId): MetricGroupDetails => {
      const metricDetails = metricTreeToMetricDetailsMap(this.metricTree)[
        metric
      ];
      const metricGroupDetails = metricTreeToMetricGroupDetailsMap(
        this.metricTree
      )[metricDetails.metricGroupId];
      if (!metricGroupDetails) {
        throw new Error(`Metric group details for metric ${metric} not found`);
      } else {
        return metricGroupDetails;
      }
    }
  );

  public getMetricDetailsFromMetricId = memoize(
    (metric: GraphQLMetricId): MetricDetails => {
      const metricDetails = metricTreeToMetricDetailsMap(this.metricTree)[
        metric
      ];
      if (!metricDetails) {
        throw new Error(`Metric group details for metric ${metric} not found`);
      } else {
        return metricDetails;
      }
    }
  );

  public getMetricsForMetricGroup = (
    metric: MetricGroupId
  ): MetricDetails[] => {
    const metricDetails = this.metricTree.groupDetails.find(
      (m) => m.id === metric
    );
    if (!metricDetails) {
      throw new Error(`invalid metric id - ${metric}`);
    } else {
      return metricDetails.dimensions;
    }
  };

  public getAllowedSortedMetricIdsForMetricGroup = (
    metricGroupId: MetricGroupId
  ): MetricDetails[] => {
    const metricDetails =
      this.allMetricGroupDetailsWithAllowedSortedMetricIds.find(
        (m) => m.id === metricGroupId
      );
    if (!metricDetails) {
      // TODO: This might happen if some invalid id is in store
      // should have a better handling
      throw new Error(`invalid metric group id - ${metricGroupId}`);
    } else {
      const sortedMetricIds = sortBy(
        metricDetails.dimensions,
        (d) => d.defaultPosition
      );
      return sortedMetricIds;
    }
  };

  public isMetricGroupRegular = (metric: MetricGroupId) => {
    return this.getMetricsForMetricGroup(metric).some(
      (dim) => dim.metricType === MetricType.MetricTypeRegular
    );
  };

  public isMetricGroupCohort = (metric: MetricGroupId) => {
    return this.getMetricsForMetricGroup(metric).some(
      (dim) => dim.metricType === MetricType.MetricTypeCohort
    );
  };

  public isMetricGroupEmployeeCohort = (metric: MetricGroupId) => {
    return this.getMetricsForMetricGroup(metric).some(
      (dim) => dim.metricType === MetricType.MetricTypeEmployeeCohort
    );
  };

  public isMetricRegular = (metric: GraphQLMetricId) => {
    return (
      this.getMetricDetailsFromMetricId(metric).metricType ===
      MetricType.MetricTypeRegular
    );
  };

  public isMetricCohort = (metric: GraphQLMetricId) => {
    return (
      this.getMetricDetailsFromMetricId(metric).metricType ===
      MetricType.MetricTypeCohort
    );
  };

  public isMetricEmployeeCohort = (metric: GraphQLMetricId) => {
    return (
      this.getMetricDetailsFromMetricId(metric).metricType ===
      MetricType.MetricTypeEmployeeCohort
    );
  };

  /**
   * Checks if a metric is active and available for the current domain and user.
   * A metric is considered active and available if:
   * 1. Its lifecycle stage is Active or Deprecated (not Disabled or Development)
   * 2. It is in the domain's allowed metrics list
   * 3. The user has permission to view it
   */
  public isMetricActiveAndAvailable = memoize(
    (metricId: GraphQLMetricId): boolean => {
      try {
        const metricDetails = this.getMetricDetailsFromMetricId(metricId);

        const isActiveOrDeprecated =
          metricDetails.lifecycleStage.__typename ===
            MetricLifecycleStageEnum.MetricLifecycleStageActive ||
          metricDetails.lifecycleStage.__typename ===
            MetricLifecycleStageEnum.MetricLifecycleStageDeprecated;

        const isDomainAllowed = this.domainAllowedMetricsSet.has(metricId);

        const isPermissionsAllowed =
          this.permissionsStore.canViewMetric(metricId);

        return isActiveOrDeprecated && isDomainAllowed && isPermissionsAllowed;
      } catch (error) {
        // If metric details are not found, return false
        return false;
      }
    }
  );

  public isMetricFinancial = (metric: MetricDetails) => {
    return metric.underlyingFields?.some(
      (f) => f.dataType === DataTypes.FINANCIALMETRICS
    );
  };

  public isMetricGroupFinancial = (metricGroup: MetricGroupId) => {
    return this.getMetricsForMetricGroup(metricGroup).some((dim) =>
      this.isMetricFinancial(dim)
    );
  };

  public metricGroupNeedsEmptyCohortFilter = (metricGroupId: MetricGroupId) => {
    const firstMetric = this.getMetricsForMetricGroup(metricGroupId)[0];
    if (!firstMetric) {
      throw new Error(`invalid metric group id - ${metricGroupId}`);
    } else {
      return metricIdNeedsEmptyCohortFilter(firstMetric.id);
    }
  };

  @computed
  public get defaultMetricGroupCategorySettings(): {
    metricGroup: MetricGroupId;
    category: MetricCategoryId;
  }[] {
    return this.metricTree.groupDetails.map((mg) => {
      return { metricGroup: mg.id, category: mg.defaultCategory };
    });
  }

  @computed
  public get effectiveMetricGroupCategorySettings(): {
    metricGroup: MetricGroupId;
    category: MetricCategoryId;
  }[] {
    const metricGroupCategorySettingsAsMap = Object.fromEntries(
      (this.companySettingsStore.metricGroupCategorySettings ?? []).map(
        (settings) => {
          return [settings.metricGroup, settings.category];
        }
      )
    );
    const defaultMetricGroupCategorySettingsAsMap = Object.fromEntries(
      (this.defaultMetricGroupCategorySettings ?? []).map((settings) => {
        return [settings.metricGroup, settings.category];
      })
    );
    const effectiveMetricGroupCategorySettings =
      this.metricTree.groupDetails.map((mg) => {
        return {
          metricGroup: mg.id,
          category:
            metricGroupCategorySettingsAsMap[mg.id] ??
            defaultMetricGroupCategorySettingsAsMap[mg.id],
        };
      });
    return effectiveMetricGroupCategorySettings;
  }

  public getCategoryForMetricGroup = (metricGroup: MetricGroupId) => {
    const domainSettingMetric = this.effectiveMetricGroupCategorySettings.find(
      (m) => m.metricGroup === metricGroup
    );
    if (!domainSettingMetric?.category) {
      throw new Error('Invalid state');
    }
    return domainSettingMetric.category;
  };

  public getCategoryForMetric = (metric: GraphQLMetricId) => {
    const metricGroupDetails = this.getMetricGroupDetailsFromMetricId(metric);
    return this.getCategoryForMetricGroup(metricGroupDetails.id);
  };

  public formatMetricValue = (
    val: number | string | null,
    metricId: GraphQLMetricId,
    header?: string
  ) => {
    const metricFormatType =
      this.getMetricDetailsFromMetricId(metricId).formatType;
    if (val === null) {
      return DATA_NOT_AVAILABLE_VALUE;
    }
    if (header === DELTA_PERCENTAGE) {
      return formatPercentageNumber(Number(val));
    }
    if (
      this.isTimeFormatToggleAvailable(metricId) &&
      this.getTimeDisplayFormat(metricId) === TimeDisplayFormat.HH_MM &&
      isValidNumber(val)
    ) {
      return convertDecimalHoursToHHMM(Number(val));
    }
    return metricFormatTypeToFormatterMap[metricFormatType](val);
  };
}
